/**
 * 同意書 (consent_documents) の hooks。
 *
 * v3.2.25 / Phase 2 (同意書管理画面)。
 *   - 一覧 / 詳細取得は Firestore 直接、書込は Callable 経由
 *   - 提出済み記録 (consent_submissions) は別 hook で扱う
 */
import type {
  ConsentDocument,
  ConsentDocumentStatus,
  ConsentImagePosition,
  ConsentPublicLink,
  ConsentSection,
  ConsentSubmission,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useMemo } from 'react';

import type { DealRecord } from '@/hooks/useDeals';
import { useProductCategories } from '@/hooks/useProductCategories';
import { db, functions } from '@/lib/firebase';
import { toDate } from '@/lib/firestoreTime';

export interface ConsentDocumentRecord extends ConsentDocument {
  id: string;
}

export function useConsentDocuments() {
  return useQuery({
    queryKey: ['consent_documents'],
    queryFn: async (): Promise<ConsentDocumentRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'consent_documents'), orderBy('updatedAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ConsentDocument),
      }));
    },
  });
}

export function useConsentDocument(id: string | null | undefined) {
  return useQuery({
    queryKey: ['consent_documents', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<ConsentDocumentRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'consent_documents', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as ConsentDocument) };
    },
  });
}

interface UpsertInput {
  id?: string;
  title: string;
  status: ConsentDocumentStatus;
  primaryCategoryId?: string | null;
  sections: ConsentSection[];
  logoStoragePath?: string | null;
  logoPosition?: ConsentImagePosition | null;
  stampStoragePath?: string | null;
  stampPosition?: ConsentImagePosition | null;
  stampHidden?: boolean;
}

const upsertConsentDocumentFn = httpsCallable<UpsertInput, { id: string }>(
  functions,
  'upsertConsentDocument',
);

export function useUpsertConsentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertInput) => {
      const r = await upsertConsentDocumentFn(input);
      return r.data;
    },
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['consent_documents'] });
      if (input.id) {
        qc.invalidateQueries({
          queryKey: ['consent_documents', 'detail', input.id],
        });
      }
    },
  });
}

const importPdfFn = httpsCallable<
  { pdfBase64: string; pdfName?: string },
  { sections: ConsentSection[]; pdfTextPreview: string }
>(functions, 'importConsentDocumentFromPdf', { timeout: 300_000 });

export function useImportConsentDocumentFromPdf() {
  return useMutation({
    mutationFn: async (input: { pdfBase64: string; pdfName?: string }) => {
      const r = await importPdfFn(input);
      return r.data;
    },
  });
}

const deleteConsentDocumentFn = httpsCallable<{ id: string }, { id: string }>(
  functions,
  'deleteConsentDocument',
);

export function useDeleteConsentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteConsentDocumentFn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consent_documents'] }),
  });
}

// ─── 公開リンク発行 + メール送信 (Phase 3c / 6: dealId 任意化) ──────────────
const issuePublicLinkFn = httpsCallable<
  { dealId?: string | null; consentDocumentId: string },
  {
    linkId: string;
    publicUrl: string;
    mailSent: boolean;
    mailReason?: string;
    preview: boolean;
  }
>(functions, 'issueConsentPublicLink');

export function useIssueConsentPublicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId?: string | null; consentDocumentId: string }) => {
      const r = await issuePublicLinkFn(input);
      return r.data;
    },
    onSuccess: (_, input) => {
      if (input.dealId) {
        qc.invalidateQueries({
          queryKey: ['consent_public_links', input.dealId],
        });
        qc.invalidateQueries({
          queryKey: ['deals', 'activities', input.dealId],
        });
      }
      qc.invalidateQueries({
        queryKey: ['consent_public_links_by_doc', input.consentDocumentId],
      });
    },
  });
}

// ─── Deal ごとの公開リンク一覧 (Phase 3c 同意タブ用) ──────────────
export interface ConsentPublicLinkRecord extends ConsentPublicLink {
  id: string;
}

export function useConsentPublicLinksByDeal(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['consent_public_links', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<ConsentPublicLinkRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'consent_public_links'), where('dealId', '==', dealId)),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ConsentPublicLink),
      }));
    },
  });
}

/** v3.2.26: 同意書テンプレ ID で公開リンク一覧を取得 (編集画面のリンクタブで使用) */
export function useConsentPublicLinksByDocument(consentDocumentId: string | null | undefined) {
  return useQuery({
    queryKey: ['consent_public_links_by_doc', consentDocumentId],
    enabled: !!consentDocumentId,
    queryFn: async (): Promise<ConsentPublicLinkRecord[]> => {
      if (!consentDocumentId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'consent_public_links'),
          where('consentDocumentId', '==', consentDocumentId),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ConsentPublicLink),
      }));
    },
  });
}

// ─── Deal ごとの提出済み同意書 (Phase 3b 同意確認ラベル用) ──────────────
export interface ConsentSubmissionRecord extends ConsentSubmission {
  id: string;
}

export function useConsentSubmissionsByDeal(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['consent_submissions', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<ConsentSubmissionRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'consent_submissions'), where('dealId', '==', dealId)),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ConsentSubmission),
      }));
    },
  });
}

// ─── 顧客ごとの提出済み同意書 (同意ステップの要否判定用, 2026-10-16) ──────────────
/**
 * 署名者 (ConsentSubmission.contactId) で引くため、提出元の商談が統合・削除された後も
 * その顧客のサイン履歴として数えられる。
 */
export function useConsentSubmissionsByContact(contactId: string | null | undefined) {
  return useQuery({
    queryKey: ['consent_submissions_by_contact', contactId],
    enabled: !!contactId,
    queryFn: async (): Promise<ConsentSubmissionRecord[]> => {
      if (!contactId) return [];
      const snap = await getDocs(
        query(collection(db, 'consent_submissions'), where('contactId', '==', contactId)),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ConsentSubmission),
      }));
    },
  });
}

export interface DealConsentRequirement {
  /** 同意ステップ (同意書タブ・ステッパーの「同意」・請求カードの同意確認) を出すか。 */
  requiresConsent: boolean;
  /**
   * 主軸カテゴリーは同意ステップ有りだが、過去のサインがあるため省略したとき、そのサイン
   * (同じ顧客・同じ主軸カテゴリーの別の商談での最新の提出)。省略していなければ null。
   */
  waivedBy: ConsentSubmissionRecord | null;
}

/**
 * 商談で同意ステップを出すかを判定する (2026-10-16)。
 *
 * - 主軸カテゴリーの requiresConsent が OFF → 出さない (成約 → 入金 直行)
 * - ON でも、同じ顧客が同じ主軸カテゴリーの **別の商談** で同意書にサイン済みなら出さない。
 *   例: TimeWaver の商談で同意書にサインした顧客の、別の TimeWaver 商談は同意書なしで進める。
 * - この商談自体にサイン済みの同意書があるときは従来どおり出す (サインした商談では証跡を見せる)。
 *
 * サイン履歴の読み込み中は出さない (カテゴリーの読み込み中と同じく、読み込み後に出す)。
 * 読み込みに失敗したときはカテゴリーの設定どおり出す。
 */
export function useDealConsentRequirement(
  deal: Pick<DealRecord, 'id' | 'contactId' | 'primaryCategoryId'> | null | undefined,
): DealConsentRequirement {
  const { data: categories } = useProductCategories({ includeInactive: true });
  const categoryRequiresConsent =
    !!deal?.primaryCategoryId &&
    (categories ?? []).some((c) => c.id === deal?.primaryCategoryId && c.requiresConsent === true);
  const own = useConsentSubmissionsByDeal(categoryRequiresConsent ? deal?.id : null);
  const byContact = useConsentSubmissionsByContact(
    categoryRequiresConsent ? deal?.contactId : null,
  );

  return useMemo(() => {
    if (!deal || !categoryRequiresConsent) return { requiresConsent: false, waivedBy: null };
    if (own.isError || byContact.isError) return { requiresConsent: true, waivedBy: null };
    const contactLoaded = !deal.contactId || byContact.isSuccess;
    if (!own.isSuccess || !contactLoaded) return { requiresConsent: false, waivedBy: null };
    if (own.data.length > 0) return { requiresConsent: true, waivedBy: null };

    const prior = (byContact.data ?? [])
      .filter(
        (s) =>
          s.dealId !== deal.id &&
          s.dealId !== 'preview' &&
          s.primaryCategoryId === deal.primaryCategoryId,
      )
      .sort(
        (a, b) => (toDate(b.submittedAt)?.getTime() ?? 0) - (toDate(a.submittedAt)?.getTime() ?? 0),
      )[0];
    return prior
      ? { requiresConsent: false, waivedBy: prior }
      : { requiresConsent: true, waivedBy: null };
  }, [
    deal,
    categoryRequiresConsent,
    own.isError,
    own.isSuccess,
    own.data,
    byContact.isError,
    byContact.isSuccess,
    byContact.data,
  ]);
}
